import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import httpClient from "../../utils/httpClients";
import { usePlayer } from "../../../PlayerContext";

function PlaySongs() {
  const { id } = useParams();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [playCountTracked, setPlayCountTracked] = useState(false);
  const [podcastInfo, setPodcastInfo] = useState(null);
  const playTimerRef = useRef(null);
  const videoRef = useRef(null);
  const [sharePopup, setSharePopup] = useState({ open: false, song: null });

  // New states for playlist functionality
  const [playlistModal, setPlaylistModal] = useState({
    open: false,
    song: null,
  });
  const [playlists, setPlaylists] = useState([]);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    description: "",
    coverImage: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [playlistLoading, setPlaylistLoading] = useState(false);

  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    seek,
    duration,
    triggerQueueUpdate,
    playTrackById,
    playVideoById,
    stopPlayback,
  } = usePlayer();

  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);
  const [hasInitiatedPlayback, setHasInitiatedPlayback] = useState(false);

  useEffect(() => {
    setSelectedEpisodeId(null);
    setHasInitiatedPlayback(false);
  }, [id]);

  useEffect(() => {
    if (!selectedEpisodeId && episodes.length > 0) {
      setSelectedEpisodeId(episodes[0]._id);
    }
  }, [episodes, selectedEpisodeId]);

  const selectedEpisode =
    episodes.find((episode) => episode._id === selectedEpisodeId) || null;

  const displayTrack =
    selectedEpisode || (episodes.length > 0 ? episodes[0] : null);

  const isDisplayTrackPlaying =
    hasInitiatedPlayback &&
    isPlaying &&
    currentTrack?._id &&
    currentTrack?._id === displayTrack?._id;

  // Lock background scroll when playlist modal is open
  useEffect(() => {
    if (!playlistModal.open) return;

    const scrollY = window.scrollY || 0;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPosition = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.overflow = prevOverflow;
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [playlistModal.open]);

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Fetch playlists
  const fetchPlaylists = async () => {
    try {
      const res = await httpClient.get("/user/playlist/get-playlists");
      if (res.data && res.data.success) {
        setPlaylists(res.data.data.playlist || []);
      }
    } catch (err) {
      console.error("Error fetching playlists:", err);
    }
  };

  // Open playlist modal
  const openPlaylistModal = (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaylistModal({ open: true, song });
    fetchPlaylists();
    setOpenDropdown(null);
  };

  // Close playlist modal
  const closePlaylistModal = () => {
    setPlaylistModal({ open: false, song: null });
    setShowCreateForm(false);
    setNewPlaylist({ name: "", description: "", coverImage: null });
  };

  // Create new playlist
  const createNewPlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      alert("Please enter playlist name");
      return;
    }

    try {
      setPlaylistLoading(true);
      const formData = new FormData();
      formData.append("name", newPlaylist.name);
      formData.append("description", newPlaylist.description);
      if (newPlaylist.coverImage) {
        formData.append("coverImage", newPlaylist.coverImage);
      }

      const res = await httpClient.post(
        "/user/playlist/create-playlist",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data && res.data.success) {
        // Refresh playlists
        await fetchPlaylists();
        setShowCreateForm(false);
        setNewPlaylist({ name: "", description: "", coverImage: null });

        // If there's a song selected, add it to the new playlist
        if (playlistModal.song) {
          await addToPlaylist(
            res.data.data.playlist._id,
            playlistModal.song._id,
          );
        }
      }
    } catch (err) {
      console.error("Error creating playlist:", err);
      alert("Failed to create playlist");
    } finally {
      setPlaylistLoading(false);
    }
  };

  // Add song to playlist
  const addToPlaylist = async (playlistId, songId) => {
    try {
      const params = new URLSearchParams({
        playlistId: playlistId,
        itemId: songId,
        itemType: "Music",
      });

      const res = await httpClient.post(`/user/playlist/add-item?${params}`);

      if (res.data && res.data.success) {
        alert("Episode added to playlist successfully!");
        closePlaylistModal();
      } else {
        alert("Failed to add episode to playlist");
      }
    } catch (err) {
      console.error("Error adding episode to playlist:", err);
      alert("Failed to add episode to playlist");
    }
  };

  // Handle file input for cover image
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPlaylist((prev) => ({
        ...prev,
        coverImage: file,
      }));
    }
  };

  // Fetch track data using your existing endpoint
  const fetchTrack = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await httpClient.get(`/user/music/media-track/${id}`);

      if (res.data && res.data.success) {
        const data = res.data.data;

        // Set podcast info from the track data
        setPodcastInfo({
          title: data.title,
          description: data.description,
          coverImageUrl: data.coverImageUrl,
          artist: data.artist,
        });

        // If this is a podcast with episodes, set them
        // Otherwise create a single episode from the track data
        if (data.episodes && Array.isArray(data.episodes)) {
          setEpisodes(data.episodes);
        } else {
          // Create a single episode from the track data
          setEpisodes([
            {
              _id: data._id,
              title: data.title,
              artist: data.artist,
              duration: data.duration,
              fileUrl: data.fileUrl,
              coverImageUrl: data.coverImageUrl,
              isVideo: data.isVideo || false,
              isLiked: data.isLiked || false,
              likesCount: data.likesCount || 0,
              playCount: data.playCount || 0,
              episodeNumber: 1,
            },
          ]);
        }
      } else {
        setError("Failed to fetch track data");
      }
    } catch (err) {
      console.error("Error fetching track:", err);
      setError("Error fetching track data");
    } finally {
      setLoading(false);
    }
  };

  // Alternative: Fetch episodes separately if needed
  const fetchEpisodes = async () => {
    try {
      // Use your existing endpoints to get related episodes
      // For example, if you have an endpoint to get tracks by artist or album
      const res = await httpClient.get(`/user/music/related-tracks/${id}`);

      if (res.data && res.data.success) {
        setEpisodes(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching episodes:", err);
      // Don't set error here as we might have the main track
    }
  };

  // Main fetch function
  const fetchData = async () => {
    await fetchTrack();
    // If you need to fetch additional episodes, call fetchEpisodes here
    // await fetchEpisodes();
  };

  const handleLikeDislike = async (contentId, currentAction) => {
    const newAction = currentAction === "like" ? "dislike" : "like";
    const isLiking = newAction === "like";

    // Optimistic update
    setEpisodes((prevEpisodes) =>
      prevEpisodes.map((episode) =>
        episode._id === contentId
          ? {
              ...episode,
              isLiked: isLiking,
              likesCount: isLiking
                ? (episode.likesCount || 0) + 1
                : Math.max(0, (episode.likesCount || 0) - 1),
            }
          : episode,
      ),
    );

    try {
      const res = await httpClient.post(
        `/user/play-history/like-content/${contentId}/${newAction}`,
        {},
        { skipToast: true, skipLoader: true },
      );
      if (!res.data?.success) {
        setEpisodes((prevEpisodes) =>
          prevEpisodes.map((episode) =>
            episode._id === contentId
              ? {
                  ...episode,
                  isLiked: !isLiking,
                  likesCount: isLiking
                    ? Math.max(0, (episode.likesCount || 0) - 1)
                    : (episode.likesCount || 0) + 1,
                }
              : episode,
          ),
        );
      }
    } catch (error) {
      console.error("Error updating like status:", error);
      setEpisodes((prevEpisodes) =>
        prevEpisodes.map((episode) =>
          episode._id === contentId
            ? {
                ...episode,
                isLiked: !isLiking,
                likesCount: isLiking
                  ? Math.max(0, (episode.likesCount || 0) - 1)
                  : (episode.likesCount || 0) + 1,
              }
            : episode,
        ),
      );
    }
  };

  const generateShareableUrl = (episodeId, episodeTitle) => {
    return `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}podcast-episode/${id}/episode/${episodeId}`;

    //  return `https://webmobrildemo.com/volumetv/podcast-episode/${id}/episode/${episodeId}`;
  };

  const shareOnSocialMedia = (platform, episode) => {
    const shareUrl = generateShareableUrl(episode._id, episode.title);
    const text = `Check out this podcast episode: ${episode.title}`;

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
    };

    if (urls[platform]) {
      window.open(urls[platform], "_blank", "width=600,height=400");
    }
  };

  const openSharePopup = (episode, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharePopup({ open: true, song: episode });
  };

  const closeSharePopup = () => {
    setSharePopup({ open: false, song: null });
  };

  const handleCopy = async () => {
    const text = generateShareableUrl(
      sharePopup.song._id,
      sharePopup.song.title,
    );

    try {
      await navigator.clipboard.writeText(text);
      alert("Link copied to clipboard");
    } catch (err) {
      console.error("Failed to copy: ", err);

      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";

      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand("copy");
        alert("Link copied to clipboard");
      } catch (err) {
        alert("Failed to copy text!");
      }

      document.body.removeChild(textArea);
    }
  };

  const handleQueueAction = async (
    action,
    trackId = null,
    mediaType = "audio",
  ) => {
    try {
      const payload = { action, mediaType };
      if (trackId) payload.trackId = trackId;
      const res = await httpClient.post(
        "user/queue/queue-controller",
        payload,
        { skipLoader: true, skipErrorToast: true },
      );
      if (res.data && res.data.success) {
        triggerQueueUpdate();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Queue action error:", error);
      return false;
    }
  };

  const addToQueue = async (trackId, isVideo = false) => {
    await handleQueueAction("add", trackId, isVideo ? "video" : "audio");
  };

  const playEpisode = async (episode) => {
    if (!episode?._id) return;

    setPlayCountTracked(false);
    if (playTimerRef.current) {
      clearTimeout(playTimerRef.current);
      playTimerRef.current = null;
    }

    setSelectedEpisodeId(episode._id);
    setHasInitiatedPlayback(true);

    const isSameTrack = currentTrack?._id === episode._id;
    if (isSameTrack) {
      if (!isPlaying) await togglePlayPause();
      return;
    }

    if (typeof stopPlayback === "function") {
      stopPlayback();
    }

    try {
      if (episode.isVideo) {
        await playVideoById?.(episode._id);
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(console.error);
          }
        }, 100);
      } else {
        await playTrackById(episode._id, "audio");
      }
    } catch (error) {
      console.error("Error playing episode:", error);
    }
  };

  const handleTopPlayPauseClick = async () => {
    if (!displayTrack) return;
    setHasInitiatedPlayback(true);
    if (currentTrack?._id !== displayTrack._id) {
      await playEpisode(displayTrack);
      return;
    }
    await togglePlayPause();
  };

  // Play count is recorded only by PlayerContext (single source of truth)

  useEffect(() => {
    if (videoRef.current && displayTrack?.isVideo) {
      if (isPlaying) {
        videoRef.current.play().catch(console.error);
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, displayTrack]);

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const toggleDropdown = (contentId) => {
    setOpenDropdown(openDropdown === contentId ? null : contentId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown")) setOpenDropdown(null);
      if (
        !event.target.closest(".playlist-popup") &&
        !event.target.closest(".dropdown-item")
      ) {
        closePlaylistModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getTotalListeners = () => {
    return episodes.reduce(
      (total, episode) => total + (episode.playCount || 0),
      0,
    );
  };

  if (loading) {
    return (
      <div className="content-inner pb-0 container-fluid" id="page_layout">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-inner pb-0 container-fluid" id="page_layout">
        <div className="alert alert-danger text-center">{error}</div>
        <div className="text-center mt-3">
          <button className="btn btn-primary" onClick={fetchData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item">
              <Link to="/podcasts">Podcasts</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              {podcastInfo?.title || "Podcast Episode"}
            </li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page">
            <h1>
              {podcastInfo?.title || displayTrack?.title || "Podcast Episode"}
            </h1>
            {displayTrack && (
              <span className="badge bg-primary ms-2">
                {displayTrack.isVideo ? "Video" : "Audio"}
              </span>
            )}
          </div>
        </div>

        <div className="col-12">
          <div className="release">
            <div className="release__content" style={{ marginRight: 0 }}>
              <div className="release__cover">
                <div className="video-wrapper">
                  <div className="video-container" id="video-container">
                    <div className="audio-player-container">
                      {displayTrack?.isVideo ? (
                        <video
                          ref={videoRef}
                          controls
                          src={displayTrack.fileUrl}
                          poster={displayTrack.coverImageUrl}
                          style={{
                            width: "100%",
                            maxHeight: "300px",
                            borderRadius: "8px",
                          }}
                          autoPlay={isPlaying}
                          onPlay={() => {
                            if (!isPlaying) togglePlayPause();
                          }}
                          onPause={() => {
                            if (isPlaying) togglePlayPause();
                          }}
                        />
                      ) : (
                        <div
                          className="audio-poster-container"
                          style={{ position: "relative" }}
                        >
                          <img
                            src={
                              podcastInfo?.coverImageUrl ||
                              displayTrack?.coverImageUrl ||
                              "img/covers/cover.svg"
                            }
                            alt={
                              podcastInfo?.title ||
                              displayTrack?.title ||
                              "Audio episode"
                            }
                            className="audio-poster"
                            style={{
                              width: "100%",
                              maxHeight: "300px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                          {isPlaying && currentTrack && (
                            <div
                              className="playing-indicator"
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                background: "rgba(0,0,0,0.7)",
                                borderRadius: "50%",
                                width: "60px",
                                height: "60px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i
                                className="fas fa-volume-up text-white"
                                style={{ fontSize: "24px" }}
                              ></i>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {podcastInfo?.description && (
                <div className="release__description mt-3">
                  <p>{podcastInfo.description}</p>
                </div>
              )}

              <div className="release__stat">
                <span>
                  {episodes.length}{" "}
                  {episodes.length === 1 ? "episode" : "episodes"}
                </span>
                <span>{getTotalListeners()} total listens</span>
              </div>

              <div className="d-flex justify-content-between gap-3 mt-3">
                <button
                  type="button"
                  className="release__buy open-modal"
                  onClick={handleTopPlayPauseClick}
                  disabled={!displayTrack}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i
                    className={
                      isDisplayTrackPlaying ? "fas fa-pause" : "fas fa-play"
                    }
                  ></i>
                  {isDisplayTrackPlaying ? "Pause" : "Play"}
                </button>
                <a className="release__buy open-modal">
                  <i className="far fa-heart"></i>
                  Follow
                </a>
              </div>
            </div>

            <div className="release__list">
              <ul className="main__list main__list--playlist main__list--dashbox">
                <h4 className="card-title text-capitalize episodes">
                  {episodes.length === 1 ? "Episode" : "All Episodes"}
                </h4>
                {episodes.length > 0 ? (
                  episodes.map((episode, index) => {
                    const isCurrentEpisode = currentTrack?._id === episode._id;
                    const isDropdownOpen = openDropdown === episode._id;
                    return (
                      <li
                        key={episode._id || index}
                        className={`single-item list-1 ${isCurrentEpisode ? "active-episode" : ""}`}
                      >
                        <div
                          className="single-item__cover"
                          onClick={() => playEpisode(episode)}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            src={
                              episode.coverImageUrl ||
                              podcastInfo?.coverImageUrl ||
                              "img/covers/cover.svg"
                            }
                            alt={episode.title}
                          />
                        </div>

                        <div className="single-item__title">
                          <h4
                            onClick={() => playEpisode(episode)}
                            style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span>
                                {episode.episodeNumber || index + 1}. {episode.title}
                              </span>
                              {episode.isVideo && (
                                <i className="fas fa-video text-primary small"></i>
                              )}
                            </span>
                            {isCurrentEpisode && (
                              <span
                                className="badge ms-0"
                                style={{
                                  backgroundColor: "#1db954",
                                  color: "#fff",
                                  fontSize: "0.7em",
                                  lineHeight: 1.2,
                                  padding: "4px 8px",
                                  borderRadius: "999px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  marginLeft: "auto",
                                }}
                              >
                                Now Playing
                              </span>
                            )}
                          </h4>
                          <span>
                            {episode.artist ||
                              podcastInfo?.artist ||
                              "Unknown Artist"}
                          </span>
                        </div>

                        <div className="dropdown">
                          <span
                            className={`d-flex align-items-center h5 mb-0 ${isDropdownOpen ? "show" : ""}`}
                            onClick={() => toggleDropdown(episode._id)}
                            style={{ cursor: "pointer", color: "#fff" }}
                          >
                            <i className="fas fa-ellipsis-h"></i>
                          </span>
                          <div
                            className={`dropdown-menu dropdown-menu-end ${isDropdownOpen ? "show" : ""}`}
                            style={
                              isDropdownOpen
                                ? {
                                    position: "absolute",
                                    transform: "translate(0px, 19px)",
                                    zIndex: 1000,
                                  }
                                : {}
                            }
                          >
                            <a
                              className="dropdown-item"
                              href="#!"
                              onClick={(e) => openPlaylistModal(episode, e)}
                            >
                              <span className="material-icons">
                                library_music
                              </span>
                              Add To Playlist
                            </a>
                            <a
                              className="dropdown-item"
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                addToQueue(episode._id, episode.isVideo);
                              }}
                            >
                              <span className="material-icons">
                                playlist_add
                              </span>
                              Add To Queue
                            </a>
                            <a
                              className="dropdown-item"
                              href="#!"
                              onClick={(e) => openSharePopup(episode, e)}
                            >
                              <span className="material-icons">share</span>
                              Share
                            </a>
                          </div>
                        </div>

                        <a
                          href="#!"
                          className="single-item__add"
                          onClick={(e) => {
                            e.preventDefault();
                            handleLikeDislike(
                              episode._id,
                              episode.isLiked ? "like" : "dislike",
                            );
                          }}
                        >
                          <i
                            className={
                              episode.isLiked
                                ? "fas fa-heart text-danger"
                                : "far fa-heart"
                            }
                          ></i>
                        </a>

                        <a
                          href="#!"
                          className="single-item__export"
                          onClick={(e) => openSharePopup(episode, e)}
                        >
                          <i className="fas fa-share"></i>
                        </a>

                        <span className="single-item__time">
                          {isCurrentEpisode
                            ? `${formatDuration(seek)} / ${formatDuration(duration)}`
                            : formatDuration(episode.duration)}
                        </span>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-center py-4">No episodes found</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Share Popup Modal */}
      {sharePopup.open && sharePopup.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="share-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "350px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "25px",
              }}
            >
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#aaa7a7",
                }}
              >
                Share Episode
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "#ccc",
                  fontSize: "14px",
                }}
              >
                {sharePopup.song.title}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                marginBottom: "25px",
              }}
            >
              {["facebook", "twitter", "whatsapp", "telegram"].map(
                (platform) => (
                  <button
                    key={platform}
                    onClick={() =>
                      shareOnSocialMedia(platform, sharePopup.song)
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "#ccc",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#2a2a2a",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                        transition: "background-color 0.3s",
                      }}
                    >
                      <i className={`fab fa-${platform}`}></i>
                    </div>
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </button>
                ),
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <input
                type="text"
                value={generateShareableUrl(
                  sharePopup.song._id,
                  sharePopup.song.title,
                )}
                readOnly
                style={{
                  flex: 1,
                  padding: "12px 15px",
                  backgroundColor: "#2a2a2a",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  padding: "12px 20px",
                  backgroundColor: "#1DB954",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "background-color 0.3s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#1ed760")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#1DB954")}
              >
                COPY
              </button>
            </div>

            <button
              onClick={closeSharePopup}
              style={{
                width: "100%",
                marginTop: "20px",
                padding: "12px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #444",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                transition: "background-color 0.3s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#2a2a2a")}
              onMouseOut={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Playlist Modal */}
      {playlistModal.open && playlistModal.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="playlist-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "400px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#ffff",
                }}
              >
                Add to Playlist
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "#ccc",
                  fontSize: "14px",
                }}
              >
                {playlistModal.song.title}
              </p>
            </div>

            {!showCreateForm ? (
              <>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginBottom: "20px",
                  }}
                >
                  {playlists.length > 0 ? (
                    playlists.map((playlist) => (
                      <div
                        key={playlist._id}
                        style={{
                          padding: "12px",
                          border: "1px solid #333",
                          borderRadius: "8px",
                          marginBottom: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                        onClick={() =>
                          addToPlaylist(playlist._id, playlistModal.song._id)
                        }
                      >
                        <img
                          src={
                            playlist.playlistCoverUrl ||
                            "/img/live/songDefault.png"
                          }
                          alt={playlist.name}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            {playlist.name}
                          </div>
                          {playlist.description && (
                            <div style={{ fontSize: "12px", color: "#ccc" }}>
                              {playlist.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#ccc",
                      }}
                    >
                      No playlists found
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowCreateForm(true)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1DB954",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  Create New Playlist
                </button>
              </>
            ) : (
              <div>
                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Playlist Name *
                  </label>
                  <input
                    type="text"
                    value={newPlaylist.name}
                    onChange={(e) =>
                      setNewPlaylist((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter playlist name"
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    value={newPlaylist.description}
                    onChange={(e) =>
                      setNewPlaylist((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter playlist description"
                    rows="3"
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Cover Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{
                      width: "100%",
                      padding: "8px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={createNewPlaylist}
                    disabled={playlistLoading}
                    style={{
                      flex: 1,
                      padding: "12px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: playlistLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      opacity: playlistLoading ? 0.7 : 1,
                    }}
                  >
                    {playlistLoading ? "Creating..." : "Create Playlist"}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      flex: 1,
                      padding: "12px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #444",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showCreateForm && (
              <button
                onClick={closePlaylistModal}
                style={{
                  width: "100%",
                  marginTop: "10px",
                  padding: "12px",
                  backgroundColor: "transparent",
                  color: "#ccc",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaySongs;
